<template>
  <v-card class="my-4">
    <v-card-title>
      Ad creative
      <v-spacer />
      <v-btn
        icon
        title="Reload"
        :loading="loading"
        @click="load"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-subtitle>
      The responsive display ad written for each segment. Google mixes and matches these assets, so every
      headline and description has to stand on its own. Character limits are hard — one over-length asset
      rejects the whole ad — and nothing goes live without approval here.
    </v-card-subtitle>

    <v-card-text v-if="firstLoad">
      <v-skeleton-loader type="card" />
    </v-card-text>

    <v-card-text v-else-if="error">
      <v-alert
        type="error"
        text
        dense
        class="mb-0"
      >
        {{ error }}
      </v-alert>
    </v-card-text>

    <v-expansion-panels
      v-else
      accordion
      multiple
      class="px-4 pb-4"
    >
      <v-expansion-panel
        v-for="row in rows"
        :key="row.segmentKey"
      >
        <v-expansion-panel-header>
          <div>
            <span class="font-weight-medium">{{ row.segmentName }}</span>
            <v-chip
              x-small
              class="ml-2"
              :color="statusFor(row).color"
              text-color="white"
            >
              {{ statusFor(row).label }}
            </v-chip>
            <v-chip
              v-if="errorCount(row)"
              x-small
              class="ml-1"
              color="error"
              text-color="white"
            >
              {{ errorCount(row) }} to fix
            </v-chip>
            <div class="caption grey--text">
              {{ row.segmentDescription }}
            </div>
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <!-- Ad group first. Nothing here can be uploaded without it, and it is the one field whose
               value comes from outside this app entirely. -->
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                :value="adGroupFor(row)"
                label="Google ad group"
                placeholder="customers/1234567890/adGroups/98765 — or just the ad group id"
                persistent-hint
                hint="Built by hand in Google Ads. Uploading needs it; generating and reviewing doesn't."
                dense
                outlined
                :loading="savingAdGroup === row.segmentKey"
                @change="setAdGroup(row, $event)"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-combobox
                :value="row.campaign ? row.campaign.targetUrl : null"
                :items="targetUrls"
                label="Destination URL"
                persistent-hint
                :hint="urlHint(row)"
                :error="!hasTargetUrl(row)"
                dense
                outlined
                :loading="savingCampaign === row.segmentKey"
                @change="setTargetUrl(row, $event)"
              />
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12">
              <!-- Optional, and usually left empty. A retargeting ad is aimed at a BEHAVIOUR, not at a
                   shopping interest — someone who signed up and never designed hasn't told us what they
                   came for, so narrowing their ad to one category makes it worse, not more specific. -->
              <v-autocomplete
                :value="targetValues(row)"
                :items="targetOptions"
                item-text="name"
                item-value="key"
                label="Narrow to specific designs (optional)"
                persistent-hint
                :hint="targetHint(row)"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
                dense
                outlined
                :loading="savingCampaign === row.segmentKey"
                @change="setTargets(row, $event)"
              />
            </v-col>
          </v-row>

          <div class="d-flex align-center mb-4">
            <!-- Gated on the destination, not on subjects. Google rejects an ad with no final URL
                 outright; subjects are genuinely optional here. -->
            <v-btn
              small
              color="primary"
              :loading="generating === row.segmentKey"
              :disabled="generating !== null || !hasTargetUrl(row)"
              @click="onGenerateClick(row)"
            >
              <v-icon left small>
                mdi-auto-fix
              </v-icon>
              {{ row.content ? 'Regenerate' : 'Generate' }}
            </v-btn>

            <v-btn
              v-if="row.content && row.status !== $enums.MARKETING.ITEM_STATUS_IDS.APPROVED"
              small
              outlined
              class="ml-2"
              :loading="approving === row.itemId"
              :disabled="!!errorCount(row)"
              @click="approve(row)"
            >
              Approve
            </v-btn>

            <v-btn
              v-if="row.content && dirty[row.itemId]"
              small
              text
              class="ml-2"
              :loading="savingCopy === row.itemId"
              @click="saveCopy(row)"
            >
              Save edits
            </v-btn>

            <v-spacer />

            <span
              v-if="row.status === $enums.MARKETING.ITEM_STATUS_IDS.APPROVED"
              class="caption success--text"
            >
              Approved — ready to upload once an ad group exists.
            </span>
          </div>

          <template v-if="row.content">
            <!-- Blocking problems above advisory ones: the first list is what stops the ad, the
                 second is what a Google reviewer might argue with later. -->
            <v-alert
              v-if="errorCount(row)"
              type="error"
              text
              dense
            >
              <div
                v-for="(issue, i) in issuesOf(row, 1)"
                :key="'e' + i"
              >
                {{ issue.message }}
              </div>
            </v-alert>

            <v-alert
              v-if="issuesOf(row, 2).length"
              type="warning"
              text
              dense
            >
              <div
                v-for="(issue, i) in issuesOf(row, 2)"
                :key="'w' + i"
              >
                {{ issue.message }}
              </div>
            </v-alert>

            <v-row dense>
              <v-col cols="12" md="7">
                <div class="subtitle-2 mb-2">
                  Headlines
                  <span class="caption grey--text">— max {{ limits.headline }} characters each</span>
                </div>
                <v-text-field
                  v-for="(headline, i) in row.content.shortHeadlines"
                  :key="'h' + i"
                  :value="headline"
                  dense
                  outlined
                  :counter="limits.headline"
                  :error="headline.length > limits.headline"
                  @input="setListValue(row, 'shortHeadlines', i, $event)"
                />

                <v-text-field
                  v-model="row.content.longHeadline"
                  label="Long headline"
                  dense
                  outlined
                  :counter="limits.longHeadline"
                  :error="(row.content.longHeadline || '').length > limits.longHeadline"
                  @input="markDirty(row)"
                />

                <div class="subtitle-2 mb-2 mt-2">
                  Descriptions
                  <span class="caption grey--text">— max {{ limits.description }} characters each</span>
                </div>
                <v-text-field
                  v-for="(description, i) in row.content.descriptions"
                  :key="'d' + i"
                  :value="description"
                  dense
                  outlined
                  :counter="limits.description"
                  :error="description.length > limits.description"
                  @input="setListValue(row, 'descriptions', i, $event)"
                />

                <v-text-field
                  v-model="row.content.businessName"
                  label="Business name"
                  dense
                  outlined
                  :counter="limits.businessName"
                  :error="(row.content.businessName || '').length > limits.businessName"
                  @input="markDirty(row)"
                />
              </v-col>

              <v-col cols="12" md="5">
                <div class="subtitle-2 mb-2">
                  Images
                </div>

                <!-- Shown at their true ratios with the pixel sizes spelled out: the sizes are what
                     Google validates, and they are the thing most likely to be wrong. -->
                <div class="caption grey--text">
                  Landscape — 1200 × 628
                </div>
                <v-img
                  v-if="row.content.images && row.content.images.landscape"
                  :src="row.content.images.landscape"
                  :aspect-ratio="1200 / 628"
                  class="grey lighten-3 mb-3"
                />
                <div
                  v-else
                  class="grey lighten-3 caption pa-4 text-center mb-3"
                >
                  Not generated yet
                </div>

                <div class="caption grey--text">
                  Square — 1200 × 1200
                </div>
                <v-img
                  v-if="row.content.images && row.content.images.square"
                  :src="row.content.images.square"
                  :aspect-ratio="1"
                  class="grey lighten-3"
                />
                <div
                  v-else
                  class="grey lighten-3 caption pa-4 text-center"
                >
                  Not generated yet
                </div>
              </v-col>
            </v-row>
          </template>

          <div
            v-else
            class="caption grey--text"
          >
            Nothing generated yet. Pick what the ad is about, then Generate.
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Regenerate drawer: same options as the campaign screen, because a regenerate is the same
         decision (replace the picture, the copy, or both) wherever it happens. -->
    <v-navigation-drawer
      v-model="regenDrawer"
      right
      temporary
      fixed
      width="420"
    >
      <v-card flat>
        <v-card-title>Regenerate ad</v-card-title>
        <v-card-text>
          <v-radio-group
            v-model="regenScope"
            dense
          >
            <v-radio
              :value="$enums.MARKETING.REGENERATE_SCOPE.ALL"
              label="Copy and images"
            />
            <v-radio
              :value="$enums.MARKETING.REGENERATE_SCOPE.COPY_ONLY"
              label="Copy only — keep the current images"
            />
            <v-radio
              :value="$enums.MARKETING.REGENERATE_SCOPE.IMAGE_ONLY"
              label="Images only — keep the copy exactly as written"
            />
          </v-radio-group>

          <v-checkbox
            v-model="regenUsePriorImage"
            dense
            hide-details
            label="Start from the current images"
            class="mb-2"
          />
          <v-checkbox
            v-model="regenUseTemplateArt"
            dense
            hide-details
            label="Feature one of our real designs"
            class="mb-4"
          />

          <v-textarea
            v-model="regenComments"
            label="Copy direction"
            rows="3"
            dense
            outlined
          />
          <v-textarea
            v-model="regenImageComments"
            label="Image direction"
            rows="3"
            dense
            outlined
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="regenDrawer = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="regenerate"
          >
            Regenerate
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-navigation-drawer>
  </v-card>
</template>

<script>
export default {
  name: 'RetargetingAdCreative',

  data() {
    return {
      loading: false,
      loaded: false,
      error: null,
      rows: [],
      segments: [],
      targetOptions: [],
      targetUrls: [],

      generating: null,
      approving: null,
      savingCopy: null,
      savingAdGroup: null,
      savingCampaign: null,

      // Per-item "has unsaved hand edits", so Save only appears when there is something to save.
      dirty: {},

      regenDrawer: false,
      regenRow: null,
      regenScope: 0,
      regenComments: null,
      regenImageComments: null,
      regenUsePriorImage: false,
      regenUseTemplateArt: true,

      // Mirrors AdCreativeValidator — shown as live counters so a limit is visible while typing
      // rather than discovered on save.
      limits: {
        headline: 30,
        longHeadline: 90,
        description: 90,
        businessName: 25
      }
    };
  },

  computed: {
    firstLoad() {
      return this.loading && !this.loaded;
    }
  },

  created() {
    this.load();
  },

  methods: {
    load() {
      this.loading = true;
      this.error = null;

      Promise.all([
        this.$http.get('retargeting/adCreatives'),
        this.$http.get('retargeting/segments'),
        this.$http.get('campaigns/getTargetOptions'),
        this.$http.get('campaigns/getTargetUrls')
      ])
        .then(([creatives, segments, options, urls]) => {
          this.rows = creatives.data || [];
          this.segments = segments.data || [];
          // A convenience, not a constraint — the combobox also accepts a hand-typed URL.
          this.targetUrls = urls.data || [];
          // Keyed "type:id" so a Category 5 and a Topic 5 stay distinct in the picker.
          this.targetOptions = (options.data || []).map((o) => ({
            ...o,
            key: `${o.targetType}:${o.id}`
          }));
          this.dirty = {};
          this.loaded = true;
        })
        .catch((error) => {
          this.error = error.message || 'Could not load the ad creative.';
        })
        .finally(() => {
          this.loading = false;
        });
    },

    statusFor(row) {
      if (!row.content) return { label: 'not written', color: 'grey' };
      if (row.status === this.$enums.MARKETING.ITEM_STATUS_IDS.APPROVED) {
        return { label: 'approved', color: 'success' };
      }
      return { label: 'draft', color: 'warning' };
    },

    issuesOf(row, severity) {
      return (row.issues || []).filter((i) => i.severity === severity);
    },

    errorCount(row) {
      return this.issuesOf(row, 1).length;
    },

    adGroupFor(row) {
      const segment = this.segments.find((s) => s.key === row.segmentKey);
      return segment ? segment.googleAdGroupResource : null;
    },

    targetValues(row) {
      return ((row.campaign && row.campaign.targets) || []).map((t) => `${t.targetType}:${t.targetId}`);
    },

    hasTargetUrl(row) {
      return !!(row.campaign && row.campaign.targetUrl);
    },

    targetHint(row) {
      if (!this.targetValues(row).length) {
        return 'Leave empty for a brand-level ad about MyCreativeShop — usually the right choice for retargeting. Artwork is drawn from the best of the whole catalog.';
      }
      return 'Scoped: copy stays within these subjects and the artwork is staged around their real published designs.';
    },

    urlHint(row) {
      if (!this.hasTargetUrl(row)) {
        return 'Required — Google rejects an ad with no landing page. Pick one, or type any mycreativeshop.com URL.';
      }
      return this.targetValues(row).length
        ? 'Where the ad lands. Filled from the subjects below if you leave it blank.'
        : 'Where the ad lands.';
    },

    // The campaign is created on the first EDIT, not on the first Generate. Creating it at generate
    // time meant the subject picker and destination field had nothing to bind to until after a
    // generation had already run — so the first ad was always written with no subject and no
    // destination, which is exactly what they're for.
    ensureCampaign(row) {
      if (row.campaign) return Promise.resolve(row.campaign);

      return this.$http
        .post('retargeting/ensureAdCampaign', { segmentKey: row.segmentKey })
        .then((response) => {
          row.campaign = response.data;
          row.itemId = (row.campaign.items || []).length ? row.campaign.items[0].id : row.itemId;
          return row.campaign;
        });
    },

    markDirty(row) {
      this.$set(this.dirty, row.itemId, true);
    },

    setListValue(row, field, index, value) {
      // Splice rather than assign: Vue 2 doesn't observe index assignment, so the counter and the
      // error state would go stale on the very field being typed into.
      row.content[field].splice(index, 1, value);
      this.markDirty(row);
    },

    setTargets(row, keys) {
      this.ensureCampaign(row).then((campaign) => {
        campaign.targets = (keys || []).map((key) => {
          const [targetType, targetId] = key.split(':');
          return { targetType: Number(targetType), targetId: Number(targetId) };
        });
        this.saveCampaign(row);
      });
    },

    setTargetUrl(row, value) {
      this.ensureCampaign(row).then((campaign) => {
        campaign.targetUrl = value;
        this.saveCampaign(row);
      });
    },

    setAdGroup(row, value) {
      this.savingAdGroup = row.segmentKey;
      this.$http
        .post('retargeting/setSegmentAdGroup', {
          segmentKey: row.segmentKey,
          adGroupResource: value
        })
        .then(() => {
          const segment = this.segments.find((s) => s.key === row.segmentKey);
          if (segment) segment.googleAdGroupResource = value;
        })
        .catch((error) => {
          // Reload so the field snaps back to what was actually stored — leaving a rejected value
          // on screen would read as saved.
          this.$root.$alert('Not saved', error.message || 'Could not save the ad group.');
          this.load();
        })
        .finally(() => {
          this.savingAdGroup = null;
        });
    },

    saveCampaign(row) {
      if (!row.campaign) return Promise.resolve();

      this.savingCampaign = row.segmentKey;
      return this.$http
        .post('campaigns/save', row.campaign)
        .then((response) => {
          // Take the server's copy back: Save fills the derived fields (and, on a first save, the
          // ad item), so keeping the local object would leave the panel one edit behind.
          row.campaign = response.data;
        })
        .catch((error) => {
          this.$root.$alert('Not saved', error.message || 'Could not save the campaign.');
        })
        .finally(() => {
          this.savingCampaign = null;
        });
    },

    // First generation goes straight through — there is nothing yet to direct. A regenerate opens
    // the drawer, because by then there IS something worth keeping or replacing deliberately.
    onGenerateClick(row) {
      if (!row.content) {
        this.generate(row, {});
        return;
      }

      this.regenRow = row;
      this.regenScope = this.$enums.MARKETING.REGENERATE_SCOPE.ALL;
      this.regenComments = null;
      this.regenImageComments = null;
      this.regenUsePriorImage = false;
      this.regenUseTemplateArt = true;
      this.regenDrawer = true;
    },

    regenerate() {
      this.regenDrawer = false;
      this.generate(this.regenRow, {
        comments: this.regenComments,
        imageComments: this.regenImageComments,
        scope: this.regenScope,
        usePriorImage: this.regenUsePriorImage,
        useTemplateArt: this.regenUseTemplateArt
      });
    },

    generate(row, options) {
      this.generating = row.segmentKey;

      this.ensureCampaign(row)
        .then((campaign) =>
          this.$http.post('retargeting/generateAdCreative', {
            id: campaign.id,
            comments: options.comments,
            imageComments: options.imageComments,
            scope: options.scope || 0,
            usePriorImage: !!options.usePriorImage,
            useTemplateArt: options.useTemplateArt !== false
          }))
        .then(() => this.load())
        .catch((error) => {
          this.$root.$alert('Generation failed', error.message || 'Could not generate the ad creative.');
        })
        .finally(() => {
          this.generating = null;
        });
    },

    saveCopy(row) {
      this.savingCopy = row.itemId;
      this.$http
        .post('retargeting/saveAdCreative', { itemId: row.itemId, content: row.content })
        .then((response) => {
          const data = response.data || {};
          row.content = data.content;
          row.issues = data.issues || [];
          row.status = this.$enums.MARKETING.ITEM_STATUS_IDS.DRAFT;
          this.$set(this.dirty, row.itemId, false);
        })
        .catch((error) => {
          this.$root.$alert('Not saved', error.message || 'Could not save the ad copy.');
        })
        .finally(() => {
          this.savingCopy = null;
        });
    },

    approve(row) {
      this.approving = row.itemId;
      this.$http
        .post('retargeting/approveAdItem?itemId=' + row.itemId)
        .then(() => this.load())
        .catch((error) => {
          // Approval is where the images are actually fetched and measured, so this message is
          // often the first news that a render came back the wrong size.
          this.$root.$alert('Not approved', error.message || 'Could not approve the ad.');
        })
        .finally(() => {
          this.approving = null;
        });
    }
  }
};
</script>
